import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { money, date } from '../lib/format.js';
import { Modal } from '../components/Modal.jsx';

const CATS = ['RENT','UTILITIES','SUPPLIES','SUBSCRIPTIONS','TRAVEL','MARKETING','PROFESSIONAL_SERVICES','BANK_FEES','TAXES','SALARIES','COGS','OTHER'];

export default function Expenses() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [cat, setCat] = useState('');
  const { data } = useQuery({ queryKey: ['expenses', cat], queryFn: () => api(`/api/expenses${cat ? `?category=${cat}` : ''}`) });
  const del = useMutation({ mutationFn: id => api(`/api/expenses/${id}`, { method: 'DELETE' }), onSuccess: () => qc.invalidateQueries({ queryKey: ['expenses'] }) });

  return (
    <>
      <div className="page-header">
        <h1>Expenses</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={cat} onChange={e => setCat(e.target.value)}>
            <option value="">All categories</option>
            {CATS.map(c => <option key={c}>{c}</option>)}
          </select>
          <button className="btn btn-primary" onClick={() => setCreating(true)}>New expense</button>
        </div>
      </div>
      {data && (
        <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>Total expenses: <strong>{money(data.sum?._sum?.amount || 0)}</strong></div>
          <div className="muted">Tax: {money(data.sum?._sum?.taxAmount || 0)}</div>
        </div>
      )}
      <table className="table">
        <thead>
          <tr><th>Date</th><th>Vendor</th><th>Description</th><th>Category</th><th style={{ textAlign: 'right' }}>Amount</th><th style={{ textAlign: 'right' }}>Tax</th><th>Ref</th><th></th></tr>
        </thead>
        <tbody>
          {data?.expenses?.map(e => (
            <tr key={e.id}>
              <td>{date(e.date)}</td>
              <td>{e.supplier?.name || e.vendorName || '—'}</td>
              <td>{e.description}</td>
              <td><span className="badge badge-viewer">{e.category}</span></td>
              <td style={{ textAlign: 'right' }}>{money(e.amount, e.currencyCode)}</td>
              <td style={{ textAlign: 'right' }}>{money(e.taxAmount, e.currencyCode)}</td>
              <td className="muted">{e.reference || ''}</td>
              <td><button className="btn btn-danger btn-sm" onClick={() => window.confirm('Delete expense?') && del.mutate(e.id)}>×</button></td>
            </tr>
          ))}
          {!data?.expenses?.length && <tr><td colSpan={8} className="empty">No expenses yet. <a href="/import?kind=expenses">Import from Excel</a></td></tr>}
        </tbody>
      </table>
      {creating && <ExpenseModal onClose={() => setCreating(false)} onDone={() => { qc.invalidateQueries({ queryKey: ['expenses'] }); setCreating(false); }} />}
    </>
  );
}

function ExpenseModal({ onClose, onDone }) {
  const { data: sups } = useQuery({ queryKey: ['suppliers'], queryFn: () => api('/api/expenses/suppliers/list') });
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    supplierId: '', vendorName: '',
    description: '', amount: 0, taxAmount: 0,
    currencyCode: 'EUR', category: 'OTHER', reference: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const submit = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form, amount: Number(form.amount), taxAmount: Number(form.taxAmount) };
      if (!body.supplierId) delete body.supplierId;
      await api('/api/expenses', { method: 'POST', body });
      onDone();
    } finally { setBusy(false); }
  };
  return (
    <Modal title="New expense" onClose={onClose} wide>
      <form onSubmit={submit}>
        <div className="row">
          <div className="field"><label>Date</label><input type="date" required value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
          <div className="field"><label>Supplier</label>
            <select value={form.supplierId} onChange={e => setForm(f => ({ ...f, supplierId: e.target.value }))}>
              <option value="">— (use vendor name)</option>
              {sups?.suppliers?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          {!form.supplierId && (
            <div className="field"><label>Vendor name</label><input value={form.vendorName} onChange={e => setForm(f => ({ ...f, vendorName: e.target.value }))} /></div>
          )}
        </div>
        <div className="field"><label>Description</label><input required value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
        <div className="row">
          <div className="field"><label>Amount</label><input type="number" step="0.01" required value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></div>
          <div className="field"><label>Tax</label><input type="number" step="0.01" value={form.taxAmount} onChange={e => setForm(f => ({ ...f, taxAmount: e.target.value }))} /></div>
          <div className="field"><label>Currency</label><input maxLength={3} value={form.currencyCode} onChange={e => setForm(f => ({ ...f, currencyCode: e.target.value.toUpperCase() }))} /></div>
          <div className="field"><label>Category</label>
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              {CATS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="row">
          <div className="field"><label>Reference</label><input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} /></div>
          <div className="field" style={{ flex: 2 }}><label>Notes</label><input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? <span className="spinner" /> : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
}
